/**
 * KAN-128: Cookie / analytics consent banner.
 *
 * Slides in from the bottom on first visit (when `localStorage.cookieConsent`
 * is unset). Three actions:
 *   - Accept all   → enables analytics, functional, performance
 *   - Reject all   → only functional (auth session, etc.)
 *   - Settings     → opens granular per-purpose toggles
 *
 * The decision flips a localStorage flag the PostHog init reads
 * (`lib/consent.ts`). PostHog comes up live without a reload via the
 * `onConsentChange` listener attached in `posthog.ts`.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import {
  GranularConsent,
  getCookieConsent,
  getGranularConsent,
  setCookieConsent,
  setGranularConsent,
} from '../lib/consent';
import BrandName from './BrandName';

const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [granular, setGranular] = useState<GranularConsent>(getGranularConsent());

  useEffect(() => {
    if (getCookieConsent() === null) {
      setVisible(true);
    }
  }, []);

  const acceptAll = () => {
    setCookieConsent('accepted');
    setVisible(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    setCookieConsent('rejected');
    setVisible(false);
    setShowSettings(false);
  };

  const saveGranular = () => {
    setGranularConsent(granular);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="cookie-banner"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.25 }}
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 pt-2 sm:px-6 sm:pb-6"
      >
        <div className="mx-auto max-w-3xl rounded-2xl border border-white/[0.10] bg-surface-base/95 backdrop-blur-md shadow-2xl">
          {!showSettings ? (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h2
                    id="cookie-banner-title"
                    className="text-base font-semibold text-white"
                  >
                    We use cookies
                  </h2>
                  <p
                    id="cookie-banner-description"
                    className="mt-2 text-sm text-gray-300 leading-relaxed"
                  >
                    <BrandName /> uses cookies and similar technologies to keep you signed in and to
                    measure how the product is used so we can improve predictions and the
                    experience. You can accept all, reject non-essential cookies, or choose which
                    categories are on. See our{' '}
                    <a
                      href="/privacy"
                      className="text-orange-400 underline hover:text-orange-300"
                    >
                      Privacy Policy
                    </a>{' '}
                    for details.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-lg border border-white/[0.12] bg-transparent px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/[0.06] transition-colors"
                >
                  Settings
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="rounded-lg border border-white/[0.12] bg-transparent px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/[0.06] transition-colors"
                >
                  Reject non-essential
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-brand/90 transition-colors"
                >
                  Accept all
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <h2
                  id="cookie-banner-title"
                  className="text-base font-semibold text-white"
                >
                  Cookie settings
                </h2>
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  aria-label="Close cookie settings"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-300 leading-relaxed">
                Choose which categories you're comfortable with. You can change these any time
                from your profile settings.
              </p>

              <div className="mt-4 space-y-3">
                <PurposeToggle
                  label="Functional"
                  description="Required to keep you signed in and remember your preferences. Always on."
                  checked={true}
                  disabled
                  onChange={() => {}}
                />
                <PurposeToggle
                  label="Analytics"
                  description="Helps us understand how the product is used so we can improve predictions."
                  checked={granular.analytics}
                  onChange={(v) => setGranular((g) => ({ ...g, analytics: v }))}
                />
                <PurposeToggle
                  label="Performance"
                  description="Lets us monitor speed and reliability so the site stays fast."
                  checked={granular.performance}
                  onChange={(v) => setGranular((g) => ({ ...g, performance: v }))}
                />
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={rejectAll}
                  className="rounded-lg border border-white/[0.12] bg-transparent px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/[0.06] transition-colors"
                >
                  Reject non-essential
                </button>
                <button
                  type="button"
                  onClick={saveGranular}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-brand/90 transition-colors"
                >
                  Save preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface PurposeToggleProps {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}

const PurposeToggle: React.FC<PurposeToggleProps> = ({
  label,
  description,
  checked,
  disabled,
  onChange,
}) => {
  return (
    <label
      className={`flex items-start gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 ${
        disabled ? 'opacity-80' : 'cursor-pointer hover:bg-white/[0.04]'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-offset-0"
      />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{label}</span>
          {disabled && (
            <span className="rounded bg-white/[0.08] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-300">
              Required
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-400 leading-relaxed">{description}</p>
      </div>
    </label>
  );
};

export default CookieConsentBanner;
